import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Phone, Truck } from "lucide-react";
import { MaskLines } from "./Reveal";
import { Link } from "react-router-dom";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

const VanShowcase = () => {
  const { t } = useLang();
  return (
    <div className="relative mx-auto w-full max-w-[640px] lg:max-w-none">
      <motion.img
        initial={{ x: 60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 1.4, ease: EASE }}
        src="/img/van.webp"
        alt={t.hero.imgAlt}
        data-testid="hero-van-image"
        className="relative z-10 block w-full object-contain"
      />
      <div aria-hidden className="absolute bottom-[1%] left-[6%] right-[4%] h-[7%] rounded-[50%] bg-forest/30 blur-xl" />
    </div>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBig = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const yCard = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-ink pt-[112px] md:pt-[128px]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_75%_30%,rgb(var(--brand)/0.12),transparent_70%)]" />
      <motion.div style={{ y: yBig }} aria-hidden className="font-display outline-text pointer-events-none absolute -right-[4vw] top-[8vh] select-none text-[34vw] font-black leading-none lg:text-[24vw]">
        24/7
      </motion.div>

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-14 px-5 pb-20 pt-12 md:px-10 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-20">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }} className="flex flex-wrap items-center gap-3">
            <span data-testid="hero-mobile-badge" className="inline-flex items-center gap-2 bg-brand2 px-3 py-1.5 font-mono text-[12px] font-medium uppercase tracking-[0.2em] text-cream">
              <Truck className="h-4 w-4" /> {h.mobile}
            </span>
            <span className="inline-flex items-center gap-2 border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {h.chip}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{h.tag}</span>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-display mt-8 text-[10.5vw] font-black uppercase leading-[0.9] tracking-[-0.02em] text-bone sm:text-[9vw] lg:text-[5.3vw] xl:text-[78px]">
            <MaskLines delay={0.35} lines={[h.l1, h.l2, <span key="a">{h.l3a} <span className="font-serif-i normal-case tracking-normal text-brand">{h.l3b}</span></span>]} />
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease: EASE }} className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg">
            {h.sub}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9, ease: EASE }} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <a data-testid="hero-call-button" href={PHONE_TEL} className="group inline-flex items-center justify-center gap-3 bg-brand px-7 py-4 text-ink transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-18px_rgb(var(--brand)/0.9)]">
              <Phone className="h-4 w-4" />
              <span className="font-display text-sm font-extrabold uppercase tracking-wider">{h.call} {PHONE_DISPLAY}</span>
            </a>
            <Link to="/quote" data-testid="hero-quote-button" className="group inline-flex items-center justify-center gap-3 border border-white/20 px-7 py-4 text-bone transition-colors duration-300 hover:border-brand hover:text-brand">
              <span className="font-display text-sm font-bold uppercase tracking-wider">{h.quote}</span>
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </Link>
          </motion.div>
        </div>

        <motion.div style={{ y: yCard }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4, delay: 0.5 }} className="lg:col-span-5 lg:self-center">
          <VanShowcase />
        </motion.div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="mx-auto grid max-w-[1440px] grid-cols-2 md:grid-cols-4">
          {h.stats.map(([k, v], i) => (
            <motion.div key={k} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 + i * 0.08, duration: 0.8, ease: EASE }} className={`px-5 py-6 md:px-10 ${i % 2 ? "border-l border-white/10" : ""} ${i > 1 ? "border-t border-white/10 md:border-t-0" : ""} ${i === 2 ? "md:border-l" : ""}`}>
              <div className="font-display text-2xl font-black text-sun md:text-3xl">{k}</div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{v}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
